(() => {
  const CFG = {
    // 只對包含「每日」「總量」的 eSIM 類商品啟用，避免影響其他商品
    planMustContain: ['每日', '總量'],
    unlimitedKeyword: '吃到飽', // 你的值是「吃到飽不降速」，用關鍵字即可
  };

  const DISABLED_CLASS = 'tp-step-disabled';

  function norm(s) {
    return (s || '').replace(/\s+/g, '').trim();
  }

  function getVariants(picker) {
    const s = picker.querySelector('script[type="application/json"]');
    if (!s) return null;
    try {
      const json = JSON.parse(s.textContent);
      return Array.isArray(json) ? json : (json?.variants || json?.product?.variants || null);
    } catch {
      return null;
    }
  }

  function getRadioValues(fs) {
    return Array.from(fs.querySelectorAll('input[type="radio"]')).map(i => i.value);
  }

  function getChecked(fs) {
    const r = fs.querySelector('input[type="radio"]:checked');
    return r ? r.value : '';
  }

  function clearChecked(fs) {
    fs.querySelectorAll('input[type="radio"]').forEach(i => (i.checked = false));
  }

  function setFsVisible(fs, visible) {
    fs.style.display = visible ? '' : 'none';
  }

  function setFsEnabled(fs, enabled) {
    fs.classList.toggle(DISABLED_CLASS, !enabled);
    fs.querySelectorAll('input,select,button').forEach(el => {
      if (!el.dataset.tpOrigDisabled) el.dataset.tpOrigDisabled = el.disabled ? '1' : '0';
      el.disabled = !enabled ? true : (el.dataset.tpOrigDisabled === '1');
    });
  }

  function setATCEnabled(picker, enabled) {
    const form = picker.closest('form');
    const btn = form?.querySelector('button[type="submit"], input[type="submit"]');
    if (btn) btn.disabled = !enabled;
  }

  function labelFor(input, scope) {
    return (
      scope.querySelector(`label[for="${CSS.escape(input.id)}"]`) ||
      input.closest('label') ||
      input.parentElement
    );
  }

  // 隱藏不在 allowedSet 的值；若 currentlyChecked 被隱藏，會自動取消勾選
  function filterRadios(fs, allowedSet) {
    const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
    inputs.forEach(input => {
      const ok = allowedSet.has(norm(input.value));
      const lab = labelFor(input, fs);
      if (lab) lab.style.display = ok ? '' : 'none';
      if (!ok && input.checked) input.checked = false;
    });
  }

  // 依 value 特徵找出三個 fieldset：方案/流量/天數
  function identify(picker) {
    const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
    if (fieldsets.length < 3) return null;

    // 天數：全部是數字
    const isDays = (fs) => {
      const vals = getRadioValues(fs).map(v => norm(v));
      return vals.length >= 3 && vals.every(v => /^\d+$/.test(v));
    };

    // 流量：包含 MB/GB 或 吃到飽
    const isData = (fs) => {
      const vals = getRadioValues(fs).map(v => norm(v));
      return vals.some(v => /MB|GB/i.test(v)) || vals.some(v => v.includes(CFG.unlimitedKeyword));
    };

    // 方案：包含 每日/總量
    const isPlan = (fs) => {
      const vals = getRadioValues(fs).map(v => norm(v));
      return CFG.planMustContain.every(k => vals.includes(norm(k)));
    };

    const dayFs = fieldsets.find(isDays);
    const planFs = fieldsets.find(isPlan);
    // 流量不要誤判到 plan/day
    const dataFs = fieldsets.find(fs => isData(fs) && fs !== planFs && fs !== dayFs);

    if (!planFs || !dataFs || !dayFs) return null;
    return { planFs, dataFs, dayFs };
  }

  function getOpt(v, idx) {
    // Dawn variants json 常見是 option1/option2/option3
    return v?.[`option${idx + 1}`] ?? (Array.isArray(v?.options) ? v.options[idx] : null);
  }

  function update(picker) {
    const ids = identify(picker);
    if (!ids) return;

    const { planFs, dataFs, dayFs } = ids;
    const variants = getVariants(picker);
    if (!variants) return;

    const plan = getChecked(planFs);
    const data = getChecked(dataFs);
    const day  = getChecked(dayFs);

    // Step 1：未選方案 → 只顯示方案，其它都隱藏、ATC 關閉
    if (!plan) {
      setFsVisible(dataFs, false);
      setFsVisible(dayFs, false);
      setATCEnabled(picker, false);
      return;
    }

    // Step 2：已選方案 → 顯示流量，並只顯示「該方案存在的流量」
    const isUnlimitedPlan = norm(plan).includes(norm(CFG.unlimitedKeyword));

    const allowedData = new Set(
      variants
        .filter(v => v.available !== false) // 只看可買的
        .filter(v => norm(getOpt(v, 0)) === norm(plan))
        .map(v => norm(getOpt(v, 1)))
        .filter(Boolean)
    );

    setFsVisible(dataFs, true);
    filterRadios(dataFs, allowedData);

    // 若方案切換導致 data 不存在了 → 清掉 day，一起回到 Step2
    const dataAfter = getChecked(dataFs);
    if (!dataAfter) {
      clearChecked(dayFs);
      setFsVisible(dayFs, false);
      setATCEnabled(picker, false);
      return;
    }

    // Step 3：已選流量 → 顯示天數，並只顯示「該方案+流量存在的天數」
    const allowedDays = new Set(
      variants
        .filter(v => v.available !== false)
        .filter(v => norm(getOpt(v, 0)) === norm(plan))
        .filter(v => norm(getOpt(v, 1)) === norm(dataAfter))
        .map(v => norm(getOpt(v, 2)))
        .filter(Boolean)
    );

    setFsVisible(dayFs, true);
    filterRadios(dayFs, allowedDays);

    const dayAfter = getChecked(dayFs);
    setATCEnabled(picker, !!dayAfter);
  }

  // 初始化成「步驟式」：清掉三層選取，只留第一層顯示
  function resetToStepMode(picker) {
    const ids = identify(picker);
    if (!ids) return false;

    const { planFs, dataFs, dayFs } = ids;

    clearChecked(planFs);
    clearChecked(dataFs);
    clearChecked(dayFs);

    setFsVisible(planFs, true);
    setFsVisible(dataFs, false);
    setFsVisible(dayFs, false);

    setATCEnabled(picker, false);
    return true;
  }

  function initIn(root) {
    root.querySelectorAll('variant-radios, variant-selects').forEach(picker => {
      if (picker.dataset.tpStepInit) return;

      const ids = identify(picker);
      if (!ids) return;

      picker.dataset.tpStepInit = '1';

      // 加一點 CSS（不用改主題檔）
      if (!document.getElementById('tp-step-style')) {
        const st = document.createElement('style');
        st.id = 'tp-step-style';
        st.textContent = `
          .${DISABLED_CLASS}{opacity:.35;pointer-events:none;}
        `;
        document.head.appendChild(st);
      }

      // 進來就重置成 Step 1（你要的「一定要先選方案」）
      resetToStepMode(picker);

      picker.addEventListener('change', () => update(picker));
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initIn(document);

    // quick add modal 是動態插入，用 observer 才能抓到
    const mo = new MutationObserver(muts => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (!(n instanceof HTMLElement)) continue;
          if (n.querySelector?.('variant-radios, variant-selects')) initIn(n);
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  });
})();
